import { Check, ChevronRight } from '@mui/icons-material';
import { Stack, styled, Typography } from '@mui/material';
import { DefaultLink } from '@shared/components/link';
import { I18nStore } from '@tools/i18n/store';
import { injectTSDI } from '@tools/tsdi';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';

import { StepButtons } from '../components/step-buttons';
import { SignupStore } from '../signup-store';

const CheckIcon = styled(Check)({
    position: 'absolute',
    top: 0,
    left: 0
});

const Item = styled('div')({
    position: 'relative',
    paddingLeft: 45
});

const BottomContainer = styled(Stack)(({ theme }) => ({
    textAlign: 'center',
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
        textAlign: 'left'
    }
}));

const LoginContainer = styled(Stack)(({ theme }) => ({
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
        justifyContent: 'flex-end'
    }
}));

const LoginLink = styled(DefaultLink)(({ theme }) => ({
    paddingLeft: theme.spacing(1)
}));

const LoginArrow = styled(ChevronRight)({
    verticalAlign: 'middle'
});

const StepItem = ({ text }: { text: string }) => (
    <Item>
        <CheckIcon
            color="secondary"
            fontSize="large"
            data-role="list-check-marks"
        />
        <Typography variant="subtitle2" display="inline">
            {text}
        </Typography>
    </Item>
);

export const SignupStep1 = () => {
    const { translate, language } = injectTSDI(I18nStore);
    const { goToNextStep, sendEvent, addtrackingProps } =
        injectTSDI(SignupStore);

    React.useEffect(() => {
        addtrackingProps({
            pageCategory: 'onboarding',
            pageName: 'welcome',
            lang: language
        });
    }, [addtrackingProps, language]);

    const handleNextStep = () => {
        sendEvent('enter_registration_data');
        goToNextStep();
    };

    const trackLoginClick = () => {
        sendEvent('login');
        return true;
    };

    return (
        <>
            <Stack spacing={5}>
                <Typography variant="h4">
                    {translate('signup.create.free.demo')}
                </Typography>
                <Stack spacing={1}>
                    <Typography variant="subtitle1">
                        {translate('signup.how.it.works')}:
                    </Typography>
                    <StepItem
                        text={translate('signup.description.enter.data')}
                    />
                    <StepItem
                        text={translate('signup.description.confirm.mail')}
                    />
                    <StepItem
                        text={translate('signup.description.test.software')}
                    />
                </Stack>
                <Stack spacing={2}>
                    <Typography variant="subtitle1">
                        {translate('signup.have.fun.testing')}
                    </Typography>

                    <Typography variant="subtitle2">
                        {translate('signup.your.magicline.team')}
                    </Typography>
                </Stack>
            </Stack>
            <StepButtons
                nextBtnLabel="signup.step1.next.button.label"
                data-role="next-step-button"
                onNextBtnClick={handleNextStep}
            />
            <BottomContainer spacing={2}>
                <LoginContainer spacing={1} direction="row">
                    <Typography
                        variant="subtitle2"
                        component="span"
                        align="left"
                    >
                        {`${translate('signup.already.member')} `}
                    </Typography>
                    <LoginLink page="login" onClick={trackLoginClick} keepQuery>
                        <Typography variant="subtitle1" component="span" noWrap>
                            {translate('signup.link.to.login')}
                            <LoginArrow />
                        </Typography>
                    </LoginLink>
                </LoginContainer>
                <div>
                    <Typography variant="subtitle1" component="span">
                        {`${translate('signup.know.more')} `}
                    </Typography>
                    <Typography variant="subtitle2" component="span">
                        <ReactMarkdown
                            components={{ p: React.Fragment }}
                            linkTarget="_blank"
                        >
                            {translate('signup.click.to.go.magicline')}
                        </ReactMarkdown>
                    </Typography>
                </div>
            </BottomContainer>
        </>
    );
};
